import type { DepositGate } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Alert } from '@fluxora/ui';

import { isWaiverSettled } from '../services/sign.service';
import { DepositBranch } from './DepositBranch';

/**
 * The deposit-settlement gate on the sign surface (E-SF06; ratified PO decision —
 * a REQUIRED deposit is collected BEFORE accept).
 *
 *  - NOT YET DECIDED         → a neutral "we work this out at the approval step"
 *                              note. NO outcome is claimed in either direction.
 *  - not required            → a "no deposit required" confirmation; accept is free
 *                              to proceed on the consents alone.
 *  - required + unsettled    → the inline `DepositBranch` (pay by card / stub, or
 *                              elect a §25.478 waiver). Accept stays BLOCKED (the
 *                              page disables the button) until the server reports
 *                              the gate satisfied.
 *  - required + satisfied    → a "deposit settled" confirmation; accept unblocks.
 *
 * ── COUNSEL RISK — ATTESTED ≠ VERIFIED ─────────────────────────────────────
 * When the gate was satisfied via a WAIVER (attested, not verified), the copy says
 * the waiver is "subject to verification before service begins" and NEVER presents
 * it as verified. This is counsel-gated (E-SF05 stub 24); the copy carries the
 * risk, code does not resolve it.
 */
export interface SignDepositGateProps {
  readonly candidateId: string;
  readonly gate: DepositGate;
}

export function SignDepositGate({ candidateId, gate }: SignDepositGateProps): JSX.Element {
  const { t } = useTranslation();

  // ⚠️ NEVER ASSERT A DEPOSIT OUTCOME THAT HAS NOT BEEN COMPUTED (E-SF19 P1).
  //
  // `required: false` carried two meanings the SPA could not tell apart: "credit
  // ran and you owe nothing", and "no credit has run, so there is no answer". The
  // gate is built from a candidate-keyed deposit record that is only written when
  // a deposit IS owed, so both produced `{ required:false, satisfied:true }` — and
  // this component rendered a GREEN SUCCESS ALERT reading "No deposit required —
  // you have no deposit to pay" for a shopper at funnel step `plan` who had
  // reached /sign by typing the URL. A §25.478 determination is a regulated
  // outcome; announcing one that does not exist is not a cosmetic defect.
  //
  // `decided` is the server saying whether it is answering the question at all.
  // Undecided renders a NEUTRAL note and no claim in either direction. The route
  // gate (FunnelStepGate) should mean a customer never reaches this state, but
  // this component must be honest on its own — a display gate is a courtesy and
  // this is the thing being displayed.
  if (!gate.decided) {
    return (
      <Alert variant="info" title={t('shop.sign.deposit.undecided.title')} role="status">
        {t('shop.sign.deposit.undecided.body')}
      </Alert>
    );
  }

  if (!gate.required) {
    return (
      <Alert variant="success" title={t('shop.sign.deposit.none.title')} role="status">
        {t('shop.sign.deposit.none.body')}
      </Alert>
    );
  }

  if (gate.satisfied) {
    return (
      <Alert variant="success" title={t('shop.sign.deposit.settled.title')} role="status">
        {isWaiverSettled(gate)
          ? t('shop.sign.deposit.settled.waiver')
          : t('shop.sign.deposit.settled.paid')}
      </Alert>
    );
  }

  // Required and unsettled — collect inline; accept remains blocked upstream.
  return (
    <div className="flex flex-col gap-3">
      <Alert variant="warning" title={t('shop.sign.deposit.required.title')} role="status">
        {t('shop.sign.deposit.required.body')}
      </Alert>
      {gate.deposit !== null ? (
        <DepositBranch candidateId={candidateId} deposit={gate.deposit} />
      ) : (
        <Alert variant="danger" role="alert">
          {t('shop.sign.deposit.unavailable')}
        </Alert>
      )}
    </div>
  );
}
